import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatCard } from "@/components/shared/PagePrimitives";
import { InfoTip } from "@/components/superadmin/Subscription";
import { Loader2, ChevronDown } from "lucide-react";

// Náklady na čtení dokladů (226) — obchodní pohled: kolik čtení, kolik nás stojí a kdo čte
// nejvíc vzhledem k ceně svého tarifu. Technické údaje (tokeny, strany) jsou sbalené dole.

const n0 = (v) => (v == null ? "—" : new Intl.NumberFormat("cs-CZ").format(Math.round(v)));
// Malé částky s haléři (2,40 Kč), větší zaokrouhleně (1 240 Kč).
const kc = (v) => (v == null ? "—" : `${new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: v < 100 ? 2 : 0, minimumFractionDigits: v > 0 && v < 100 ? 2 : 0 }).format(v)} Kč`);
const sec = (ms) => (ms == null ? "—" : `${(ms / 1000).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} s`);
const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const monthName = (m) => (m ? `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}` : "");
const COLS = "md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_100px_90px_90px]";

function Limit({ f }) {
  if (f.limit === null || f.limit === undefined) return <span className="text-muted-foreground">bez limitu</span>;
  if (f.limit === 0) return <span className="text-amber-700 dark:text-amber-400 font-semibold">tarif čtení nemá</span>;
  const over = f.paid > f.limit;
  return <span className={over ? "text-rose-600 dark:text-rose-400 font-semibold" : "text-muted-foreground"}>z {n0(f.limit)}{over ? " · nad limit" : ""}</span>;
}

function FirmRow({ f }) {
  const share = f.share == null ? "—" : `${f.share.toLocaleString("cs-CZ")} %`;
  return (
    <div className={`px-3.5 py-3 md:py-2.5 text-sm md:grid ${COLS} md:gap-3 md:items-center`} data-testid={`ocr-firm-${f.id}`}>
      <div className="min-w-0">
        <div className="font-semibold truncate">{f.name}{f.is_test && <span className="ml-1.5 text-[11px] font-semibold text-muted-foreground">test</span>}</div>
        <div className="text-xs text-muted-foreground truncate">{f.plan || "—"}{f.plan_price ? ` · ${n0(f.plan_price)} Kč/měs.` : ""}</div>
      </div>
      {/* mobil: čísla pod názvem */}
      <div className="md:hidden mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[13px] tabular-nums">
        <span><b>{n0(f.reads)}</b> dokladů <Limit f={f} /></span>
        <span><b>{kc(f.cost)}</b>{f.estimated ? "*" : ""}{f.paid ? ` · ${kc(f.per_doc)}/ks` : ""}</span>
        <span className="text-muted-foreground">{share} z tarifu</span>
      </div>
      <div className="hidden md:block tabular-nums"><b>{n0(f.reads)}</b> <Limit f={f} />{f.free > 0 && <div className="text-xs text-muted-foreground">zdarma {n0(f.free)}</div>}</div>
      <div className="hidden md:block text-right font-semibold tabular-nums" title={f.estimated ? "Část čtení proběhla před měřením — odhad 3 Kč za doklad" : undefined}>{kc(f.cost)}{f.estimated ? "*" : ""}</div>
      <div className="hidden md:block text-right tabular-nums text-muted-foreground">{f.paid ? kc(f.per_doc) : "—"}</div>
      <div className="hidden md:block text-right tabular-nums">{share}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-right">{value}</span>
    </div>
  );
}

function Tech({ t }) {
  const KIND = { document: "Faktury", receipt: "Účtenky" };
  return (
    <details className="group rounded-[14px] border border-border" data-testid="ocr-tech">
      <summary className="list-none cursor-pointer px-3.5 py-3 text-sm font-semibold flex items-center gap-2">
        <ChevronDown size={16} className="text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
        Technické údaje <span className="font-normal text-muted-foreground truncate">— pro levnější čtení</span>
      </summary>
      <div className="px-3.5 pb-3.5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {Object.entries(t.by_kind).map(([k, g]) => (
            <section key={k} className="rounded-[12px] bg-secondary/50 p-3 min-w-0">
              <h4 className="text-sm font-bold mb-1">{KIND[k] || k}</h4>
              <Row label="Přes AI · ISDOC · opakované" value={`${n0(g.ai)} · ${n0(g.isdoc)} · ${n0(g.cache)}`} />
              <Row label="Ø cena jednoho čtení" value={g.ai ? kc(g.avg_cost) : "—"} />
              <Row label="Ø tokenů vstup / výstup" value={g.ai ? `${n0(g.avg_in)} / ${n0(g.avg_out)}` : "—"} />
              <Row label="z toho přemýšlení" value={g.ai ? n0(g.avg_reasoning) : "—"} />
              <Row label="Ø stran · doba čtení" value={g.ai ? `${g.avg_pages.toLocaleString("cs-CZ")} · ${sec(g.ms_p50)}` : "—"} />
              {g.pdf > 0 && <Row label="PDF s textem" value={`${n0(g.pdf_with_text)} z ${n0(g.pdf)}`} />}
              {g.over_page_limit > 0 && <Row label="Delší než 15 stran" value={n0(g.over_page_limit)} />}
            </section>
          ))}
        </div>
        {t.suppliers.length > 0 && (
          <section>
            <h4 className="text-sm font-bold mb-1">Nejdražší dodavatelé</h4>
            <div className="divide-y divide-border">
              {t.suppliers.map((x, i) => (
                <div key={i} className="py-1.5 text-sm flex items-center gap-3">
                  <span className="truncate flex-1 min-w-0">{x.name}</span>
                  <span className="tabular-nums whitespace-nowrap text-muted-foreground">{n0(x.reads)}×</span>
                  <span className="tabular-nums whitespace-nowrap w-20 text-right font-semibold">{kc(x.cost)}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </details>
  );
}

export default function OcrStatsDialog({ onClose }) {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null); setError("");
    api.get(`/admin/ocr-stats${month ? `?month=${month}` : ""}`)
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(formatApiError(e)); });
    return () => { alive = false; };
  }, [month]);

  const t = data?.total;
  const prev = !!month;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="ocr-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">Náklady na čtení dokladů
            <InfoTip label="Jak se počítá">
              Platíme jen čtení umělou inteligencí, podle ceníku modelu a kurzu dolaru. ISDOC v PDF a znovu nahraný
              stejný soubor jsou zdarma. Čísla ověříme podle úbytku kreditů v Emergentu. Hvězdička = část čtení
              proběhla před měřením, počítá se odhadem 3 Kč za doklad.
            </InfoTip>
          </DialogTitle>
          <DialogDescription>Kolik stojí, když aplikace čte faktury a účtenky, a kdo čte nejvíc.</DialogDescription>
        </DialogHeader>

        <div className="inline-flex w-fit justify-self-start rounded-full border border-border bg-card p-1" role="tablist" aria-label="Měsíc">
          <button type="button" role="tab" aria-selected={!prev} onClick={() => setMonth("")} data-testid="ocr-month-cur"
            className={`h-9 px-4 rounded-full text-sm font-semibold ${!prev ? "bg-brand text-white" : "text-muted-foreground"}`}>Tento měsíc</button>
          <button type="button" role="tab" aria-selected={prev} disabled={!data && !prev} onClick={() => data && setMonth(data.prev)} data-testid="ocr-month-prev"
            className={`h-9 px-4 rounded-full text-sm font-semibold ${prev ? "bg-brand text-white" : "text-muted-foreground"}`}>Minulý měsíc</button>
        </div>

        {error && <p className="text-sm text-rose-600 dark:text-rose-400" role="alert">{error}</p>}
        {!data && !error && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Načítám…</div>
        )}

        {t && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 [&>*]:min-w-0">
              <div className="col-span-2 sm:col-span-1 grid"><StatCard label={`Náklady · ${monthName(data.month)}`} value={kc(t.cost)} sub={`${n0(t.paid)} placených čtení`} testid="ocr-cost" /></div>
              <StatCard label="Přečteno dokladů" value={n0(t.reads)} sub={t.free ? `zdarma ${n0(t.free)}` : `${n0(t.firms)} firem`} title={t.free ? `ušetřeno ${kc(t.saved)}` : undefined} testid="ocr-reads" />
              <StatCard label="Průměr na doklad" value={t.paid ? kc(t.per_doc) : "—"} sub="placené čtení" testid="ocr-per-doc" />
            </div>

            <section>
              <h4 className="text-sm font-bold mb-1.5">Kdo čte nejvíc</h4>
              {data.firms.length === 0 ? (
                <p className="text-sm text-muted-foreground rounded-[14px] border border-border px-3.5 py-4">Za tento měsíc zatím nikdo nic nečetl.</p>
              ) : (
                <div className="rounded-[14px] border border-border divide-y divide-border" data-testid="ocr-firms">
                  <div className={`hidden md:grid ${COLS} gap-3 px-3.5 py-2 text-xs font-semibold text-muted-foreground`}>
                    <span>Firma a tarif</span><span>Doklady</span><span className="text-right">Náklady</span><span className="text-right">Na doklad</span><span className="text-right">Z ceny tarifu</span>
                  </div>
                  {data.firms.map((f) => <FirmRow key={f.id} f={f} />)}
                </div>
              )}
            </section>

            <Tech t={data.tech} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
